import type {
    EntityPatch,
    HistoryDomainAdapter,
    HistoryEntitySnapshot,
    HistoryPatchApplyInput,
} from '..';
import {
    createFeatureRevision,
    type FeatureReplayValidationResult,
} from '../../feature/rebuild/FeatureReplayConsistency';
import {normalizeFeatureHistoryParameterRecord} from './FeatureHistoryPlainDataNormalizer';
import {isSerializablePlainRecord} from './SerializablePlainData';

/**
 * Feature 历史 adapter ID。
 */
export const FEATURE_HISTORY_ADAPTER_ID = 'history-domain:feature';

/**
 * Feature 定义快照。
 *
 * 设计说明：
 * 1. 该快照代表 feature 主状态，不代表 worker BRep / Mesh 派生状态；
 * 2. commandSeq / featureRevision / resultHash 只记录 replay 一致性元数据，不把派生几何写入历史。
 */
export interface FeatureDefinitionHistorySnapshot {
    /** Feature ID。 */
    readonly featureId: string;
    /** Feature Worker 命令类型。 */
    readonly commandType?: CadCommand['type'];
    /** Feature 类型。 */
    readonly featureKind: string;
    /** Feature 定义版本。 */
    readonly version: number;
    /** 可序列化参数。 */
    readonly parameters: Readonly<Record<string, unknown>>;
    /** 命令在当前 Worker feature history 中的位置。 */
    readonly commandIndex?: number;
    /** 命令创建时间戳。 */
    readonly timestamp?: number;
    /** 命令客户端 ID。 */
    readonly clientId?: string;
    /** 命令全局序号。 */
    readonly seqId?: number;
    /** 命令状态。 */
    readonly status?: CadCommand['status'];
    /** replay 命令序列哈希。 */
    readonly commandSeq?: string;
    /** feature definition revision。 */
    readonly featureRevision?: string;
    /** Worker replay 结果哈希；不得替代 feature 主状态。 */
    readonly resultHash?: string;
    /** 是否抑制。 */
    readonly suppressed?: boolean;
    /** 是否隐藏。 */
    readonly hidden?: boolean;
}

/**
 * Feature 历史运行时存储。
 */
export interface FeatureHistoryStore {
    /**
     * 写入 feature 定义。
     *
     * @param snapshot feature 定义快照。
     */
    readonly upsertFeatureDefinition: (snapshot: FeatureDefinitionHistorySnapshot) => void;
    /**
     * 删除 feature 定义。
     *
     * @param featureId feature ID。
     */
    readonly deleteFeatureDefinition: (featureId: string) => void;
}

/**
 * Feature 历史快照与 replay 校验的一致性结果。
 */
export interface FeatureHistoryReplayConsistencyResult {
    /** 是否一致。 */
    readonly consistent: boolean;
    /** 不一致原因。 */
    readonly reasons: readonly string[];
}

/**
 * 判断 feature 快照是否为 plain data。
 *
 * @param snapshot feature 定义快照。
 * @returns 是否为 plain data。
 */
export function isPlainFeatureDefinitionSnapshot(snapshot: FeatureDefinitionHistorySnapshot): boolean {
    return typeof snapshot.featureId === 'string'
        && (typeof snapshot.commandType === 'undefined' || typeof snapshot.commandType === 'string')
        && typeof snapshot.featureKind === 'string'
        && typeof snapshot.version === 'number'
        && Number.isFinite(snapshot.version)
        && isSerializablePlainRecord(snapshot.parameters)
        && (typeof snapshot.commandIndex === 'undefined'
            || (typeof snapshot.commandIndex === 'number' && Number.isFinite(snapshot.commandIndex)))
        && (typeof snapshot.timestamp === 'undefined'
            || (typeof snapshot.timestamp === 'number' && Number.isFinite(snapshot.timestamp)))
        && (typeof snapshot.clientId === 'undefined' || typeof snapshot.clientId === 'string')
        && (typeof snapshot.seqId === 'undefined'
            || (typeof snapshot.seqId === 'number' && Number.isFinite(snapshot.seqId)))
        && (typeof snapshot.status === 'undefined' || typeof snapshot.status === 'string')
        && (typeof snapshot.commandSeq === 'undefined' || typeof snapshot.commandSeq === 'string')
        && (typeof snapshot.featureRevision === 'undefined' || typeof snapshot.featureRevision === 'string')
        && (typeof snapshot.resultHash === 'undefined' || typeof snapshot.resultHash === 'string')
        && (typeof snapshot.suppressed === 'undefined' || typeof snapshot.suppressed === 'boolean')
        && (typeof snapshot.hidden === 'undefined' || typeof snapshot.hidden === 'boolean');
}

/**
 * 从命令参数中解析 feature 定义版本。
 *
 * @param command feature 命令。
 * @returns feature 定义版本。
 */
function resolveFeatureDefinitionVersion(command: CadCommand): number {
    const params = command.params as Record<string, unknown>;
    const directVersion = params.version;
    const nestedDefinition = params.definition as Record<string, unknown> | undefined;
    const nestedVersion = nestedDefinition?.version;
    const version = typeof directVersion === 'number'
        ? directVersion
        : typeof nestedVersion === 'number'
            ? nestedVersion
            : 1;

    return Number.isFinite(version) ? version : 1;
}

/**
 * 从 feature 命令构造历史快照。
 *
 * 设计说明：
 * 1. 该函数只提取命令中的 feature definition 主状态；
 * 2. Worker resultHash 只作为 replay 校验元数据保存，不包含 Mesh / BRep blob；
 * 3. 后续 undo/redo 应恢复该快照，再通过 dirtyScope 触发派生几何重建。
 *
 * @param input 快照输入。
 * @returns feature 历史快照。
 */
export function createFeatureDefinitionHistorySnapshotFromCommand(input: {
    /** feature 命令。 */
    readonly command: CadCommand;
    /** 命令在当前 Worker feature history 中的位置。 */
    readonly commandIndex?: number;
    /** replay 命令序列哈希。 */
    readonly commandSeq?: string;
    /** Worker replay 结果哈希。 */
    readonly resultHash?: string;
    /** 是否抑制。 */
    readonly suppressed?: boolean;
    /** 是否隐藏。 */
    readonly hidden?: boolean;
}): FeatureDefinitionHistorySnapshot {
    const params = normalizeFeatureHistoryParameterRecord(input.command.params as Record<string, unknown>);
    const canonicalCommand = {
        ...input.command,
        params,
    };
    const featureKind = typeof params.featureKind === 'string'
        ? params.featureKind
        : String(input.command.type);

    return {
        featureId: input.command.id,
        commandType: input.command.type,
        featureKind,
        version: resolveFeatureDefinitionVersion(input.command),
        parameters: params,
        commandIndex: input.commandIndex,
        timestamp: input.command.timestamp,
        clientId: input.command.clientId,
        seqId: input.command.seqId,
        status: input.command.status,
        commandSeq: input.commandSeq,
        featureRevision: createFeatureRevision(canonicalCommand),
        resultHash: input.resultHash,
        suppressed: input.suppressed,
        hidden: input.hidden,
    };
}

/**
 * 从 feature 历史快照重建 Worker 命令。
 *
 * 设计说明：
 * 1. Undo/Redo 恢复的是 feature 主状态，派生 BRep / Mesh 必须由 Worker replay 重新生成；
 * 2. 命令 ID 必须继续使用 featureId，保证设计树、依赖图和下游 feature 引用不漂移；
 * 3. 兼容早期未写入 commandType 的会话内快照，但新记录都应写入 commandType。
 *
 * @param snapshot feature 历史快照。
 * @returns 可提交给 Worker replay 的命令。
 * @throws 当快照不是可重放的纯数据时抛出错误。
 */
export function createFeatureCommandFromHistorySnapshot(
    snapshot: FeatureDefinitionHistorySnapshot,
): CadCommand {
    if (!isPlainFeatureDefinitionSnapshot(snapshot)) {
        throw new Error(`Feature ${snapshot.featureId} 的历史快照不是可 replay 的 plain data。`);
    }

    const command: CadCommand = {
        id: snapshot.featureId,
        type: snapshot.commandType ?? snapshot.featureKind as CadCommand['type'],
        params: structuredClone(snapshot.parameters) as Record<string, unknown>,
        timestamp: snapshot.timestamp ?? Date.now(),
        clientId: snapshot.clientId ?? 'web-client',
    };

    if (typeof snapshot.seqId !== 'undefined') {
        command.seqId = snapshot.seqId;
    }

    if (typeof snapshot.status !== 'undefined') {
        command.status = snapshot.status;
    }

    return command;
}

/**
 * 将 feature 快照应用到 Worker 命令历史。
 *
 * @param input 应用输入。
 * @returns 应用后的命令历史。
 * @throws 当目标快照标记为存在但缺少数据，或快照数据无法重放时抛出错误。
 */
export function applyFeatureSnapshotToCommandHistory(input: {
    /** 当前 Worker 命令历史。 */
    readonly commands: readonly CadCommand[];
    /** 待恢复的 feature ID。 */
    readonly featureId: string;
    /** PatchApplier 目标快照。 */
    readonly snapshot: HistoryEntitySnapshot<FeatureDefinitionHistorySnapshot>;
}): CadCommand[] {
    const clonedCommands = input.commands.map((command) => structuredClone(command));
    const existingIndex = clonedCommands.findIndex((command) => command.id === input.featureId);

    if (!input.snapshot.exists) {
        return clonedCommands.filter((command) => command.id !== input.featureId);
    }

    if (!input.snapshot.data) {
        throw new Error(`Feature ${input.featureId} 缺少可用于 replay 的历史快照。`);
    }

    const nextCommand = createFeatureCommandFromHistorySnapshot(input.snapshot.data);

    if (existingIndex >= 0) {
        return clonedCommands.map((command, index) => (
            index === existingIndex ? nextCommand : command
        ));
    }

    const requestedIndex = input.snapshot.data.commandIndex ?? clonedCommands.length;
    const insertIndex = Math.max(0, Math.min(requestedIndex, clonedCommands.length));

    return [
        ...clonedCommands.slice(0, insertIndex),
        nextCommand,
        ...clonedCommands.slice(insertIndex),
    ];
}

/**
 * 校验 feature 历史快照是否匹配 replay 结果。
 *
 * 设计说明：
 * ChangeSet 中的 feature patch 保存主状态，replay 结果保存派生重建摘要。
 * 两者不能互相替代，但必须通过 commandSeq / featureRevision / resultHash 建立一致性检查点，
 * 避免 redo 后 Worker replay 成功却与实际提交的 feature definition 不一致。
 *
 * @param snapshot feature 历史快照。
 * @param replay replay 校验结果。
 * @returns 一致性结果。
 */
export function validateFeatureHistorySnapshotAgainstReplay(
    snapshot: FeatureDefinitionHistorySnapshot,
    replay: FeatureReplayValidationResult,
): FeatureHistoryReplayConsistencyResult {
    const reasons: string[] = [];

    if (replay.status !== 'valid') {
        reasons.push(`replay 状态不是 valid：${replay.status}`);
    }

    if (replay.featureId && snapshot.featureId !== replay.featureId) {
        reasons.push(`featureId 不一致：snapshot=${snapshot.featureId}, replay=${replay.featureId}`);
    }

    if (snapshot.commandSeq && snapshot.commandSeq !== replay.commandSeq) {
        reasons.push(`commandSeq 不一致：snapshot=${snapshot.commandSeq}, replay=${replay.commandSeq}`);
    }

    if (snapshot.featureRevision && snapshot.featureRevision !== replay.featureRevision) {
        reasons.push(`featureRevision 不一致：snapshot=${snapshot.featureRevision}, replay=${replay.featureRevision ?? 'missing'}`);
    }

    if (snapshot.resultHash && snapshot.resultHash !== replay.resultHash) {
        reasons.push(`resultHash 不一致：snapshot=${snapshot.resultHash}, replay=${replay.resultHash}`);
    }

    return {
        consistent: reasons.length === 0,
        reasons,
    };
}

/**
 * 应用 feature 定义快照。
 *
 * @param input 补丁应用输入。
 */
function applyFeatureSnapshot(
    input: HistoryPatchApplyInput<FeatureDefinitionHistorySnapshot, FeatureHistoryStore>,
): void {
    const snapshot = input.snapshot;

    if (!snapshot.exists) {
        input.context.deleteFeatureDefinition(input.patch.entityId);

        return;
    }

    if (!snapshot.data) {
        throw new Error(`Feature ${input.patch.entityId} 缺少快照数据。`);
    }

    if (!isPlainFeatureDefinitionSnapshot(snapshot.data)) {
        throw new Error(`Feature ${input.patch.entityId} 的快照不是可序列化 plain data。`);
    }

    input.context.upsertFeatureDefinition(snapshot.data);
}

/**
 * 比较 feature 快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 是否语义相等。
 */
function areFeatureSnapshotsEqual(
    left: HistoryEntitySnapshot<FeatureDefinitionHistorySnapshot>,
    right: HistoryEntitySnapshot<FeatureDefinitionHistorySnapshot>,
): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * 创建 feature 历史 adapter。
 *
 * @returns feature 历史 adapter。
 */
export function createFeatureHistoryAdapter(): HistoryDomainAdapter<FeatureDefinitionHistorySnapshot, FeatureHistoryStore> {
    return {
        adapterId: FEATURE_HISTORY_ADAPTER_ID,
        kind: 'feature',
        applySnapshot: applyFeatureSnapshot,
        areSnapshotsEqual: areFeatureSnapshotsEqual as HistoryDomainAdapter<
            FeatureDefinitionHistorySnapshot,
            FeatureHistoryStore
        >['areSnapshotsEqual'],
    };
}

/**
 * 创建 feature 历史补丁。
 *
 * @param patch 补丁字段。
 * @returns feature 历史补丁。
 */
export function createFeatureDefinitionHistoryPatch(
    patch: Omit<EntityPatch<FeatureDefinitionHistorySnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<FeatureDefinitionHistorySnapshot> {
    return {
        ...patch,
        entityKind: 'feature',
        adapterId: FEATURE_HISTORY_ADAPTER_ID,
    };
}
